/**
 * A tax number by the country the company is registered in (D-199), checked as Core checks it
 * (TaxId): Poland's NIP by its check digit, an EU VAT number by its country's format.
 */
export const EU = {
  AT: 'U\\d{8}', BE: '[01]\\d{9}', BG: '\\d{9,10}', CY: '\\d{8}[A-Z]', CZ: '\\d{8,10}', DE: '\\d{9}', DK: '\\d{8}', EE: '\\d{9}', GR: '\\d{9}',
  ES: '[A-Z0-9]\\d{7}[A-Z0-9]', FI: '\\d{8}', FR: '[A-HJ-NP-Z0-9]{2}\\d{9}', HR: '\\d{11}', HU: '\\d{8}', IE: '\\d[A-Z0-9+*]\\d{5}[A-Z]{1,2}', IT: '\\d{11}',
  LT: '\\d{9}|\\d{12}', LU: '\\d{8}', LV: '\\d{11}', MT: '\\d{8}', NL: '\\d{9}B\\d{2}', PL: '\\d{10}', PT: '\\d{9}', RO: '\\d{2,10}', SE: '\\d{12}', SI: '\\d{8}', SK: '\\d{10}',
}

export const inEu = (country) => !!EU[String(country || '').toUpperCase()]
const viesCode = (country) => (country === 'GR' ? 'EL' : country)

/** As Core keeps it: a Polish NIP's digits; an EU number with its prefix; anything else trimmed. */
export function normalizeTaxId(country, value) {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const c = String(country || '').toUpperCase()
  const compact = raw.toUpperCase().replace(/[^0-9A-Z+*]/g, '')
  if (c === 'PL') return compact.replace(/^PL/, '')
  if (inEu(c)) return viesCode(c) + compact.replace(new RegExp(`^(${viesCode(c)}|${c})`), '')
  return raw
}

export function nipChecks(nip) {
  if (!/^\d{10}$/.test(nip)) return false
  const sum = [6, 5, 7, 2, 3, 4, 5, 6, 7].reduce((s, w, i) => s + w * Number(nip[i]), 0)
  return sum % 11 !== 10 && sum % 11 === Number(nip[9])
}

/** Why the number is not the country's (an i18n key and its values); null when it is. */
export function taxIdProblem(country, value) {
  const c = String(country || '').toUpperCase()
  const n = normalizeTaxId(c, value)
  if (!n) return null
  if (c === 'PL') {
    if (!/^\d{10}$/.test(n)) return { key: 'registry.plDigits' }
    return nipChecks(n) ? null : { key: 'registry.plCheck' }
  }
  if (inEu(c)) return new RegExp(`^(?:${EU[c]})$`).test(n.slice(2)) ? null : { key: 'registry.euFormat', values: { country: c } }
  return null
}

/** A registry to ask: the white list for Poland, VIES for the rest of the EU, none elsewhere. */
export const registryOf = (country) => (String(country || '').toUpperCase() === 'PL' ? 'mf' : inEu(country) ? 'vies' : null)

/** A Polish NIP as it is written: 774-000-14-54. */
export const displayTaxId = (country, value) => (String(country || '').toUpperCase() === 'PL' && /^\d{10}$/.test(value || '') ? `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 8)}-${value.slice(8)}` : value || '')
